import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { CreateVirtualCardScreen } from 'src/app/CreateVirtualCardForm/CreateVirtualCardScreen'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import { CustomVirtualCardAddressFlatFields } from 'src/components/CustomVirtualCardAddressBox/CustomVirtualCardAddressBox'
import { PhysicalVirtualCardAddressFlatFields } from 'src/components/PhysicalVirtualCardBox/PhysicalVirtualCardBox'
import { RecurrenceFormValues } from 'src/components/RecurrenceForm/RecurrenceForm'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import VirtualCardForm, {
  BillPayRecipient,
  VirtualCardFlatFormValues,
} from 'src/components/VirtualCardForm/VirtualCardForm'
import { ImageNames, OrganizationRoles, VirtualCardTypes } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PostVirtualCardRequest } from 'src/requests/postVirtualCard.request'
import { toCents } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'

interface CreateVirtualCardBillPayContentProps {
  isFormDisabled: boolean
  labelCreditCardsAsAccounts: boolean
  creditCardOptions: CreditCardOption[]
  receiptAttachments: ReceiptAttachmentModel[]
  uploadingAttachmentThumbnails: Record<string, string>
  postingVirtualCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  deleteAttachment: (attachmentId: string) => void
  dispatchPostBillPayVirtualCardAndOpenToastAndCloseFlyout: (
    body: Partial<PostVirtualCardRequest>,
  ) => void
  setScreen: (val: CreateVirtualCardScreen) => void
  userInfiniteScrollProps: UserDropdownInfiniteScrollProps
  creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps
  pageLocation: string
  showSoftAlert?: boolean
}

const CreateVirtualCardBillPayContent: React.FC<
  CreateVirtualCardBillPayContentProps
> = (props) => {
  const queryParams = useSelector(selectQueryParams)
  const { analytics } = useAnalyticsContext()
  const { data: user } = useQueryOwnUser({})
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const guestCardImage = useColorOrGrayscaleImage(ImageNames.CardTypeGuest)

  const onSuccessfulSubmit = (
    flatFields: VirtualCardFlatFormValues,
    expenseDetails: Record<string, string>,
    _: RecurrenceFormValues,
    customAddressFields: CustomVirtualCardAddressFlatFields,
    physicalAddressFields: PhysicalVirtualCardAddressFlatFields,
    __?: OrganizationRoles,
  ): void => {
    const fields = {
      creditCardId: flatFields.creditCardId,
      displayName: flatFields.vendorName,
      expenseDetails: Object.entries(expenseDetails)
        .filter((keyVal) => !!keyVal[1])
        .map(([key, val]) => ({ categoryId: key, labelId: val })),
      balanceCents:
        flatFields.balanceDollars !== undefined
          ? toCents(flatFields.balanceDollars)
          : undefined,
      currency: flatFields.currency,
      notes: flatFields.notes,
      validTo:
        flatFields.validToMoment !== undefined
          ? flatFields.validToMoment.format('YYYY-MM-DD')
          : undefined,
      receiptAttachmentIds: props.receiptAttachments.map((att) => att.id),
      // TODO: update when we add receipt rules to bill pay
      receiptRulesExempt: true,
      address1: flatFields.customBillingAddress
        ? customAddressFields.address1
        : undefined,
      address2: flatFields.customBillingAddress
        ? customAddressFields.address2
        : undefined,
      city: flatFields.customBillingAddress
        ? customAddressFields.city
        : undefined,
      province: flatFields.customBillingAddress
        ? customAddressFields.province
        : undefined,
      postal: flatFields.customBillingAddress
        ? customAddressFields.postal
        : undefined,
      country: flatFields.customBillingAddress
        ? customAddressFields.country
        : undefined,
      hasPlasticCard: flatFields.includePhysicalCard,
      plasticFirstName: flatFields.includePhysicalCard
        ? flatFields.plasticFirstName
        : undefined,
      plasticLastName: flatFields.includePhysicalCard
        ? flatFields.plasticLastName
        : undefined,
      plasticAddress1:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.address1
          : undefined,
      plasticAddress2:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.address2
          : undefined,
      plasticCity:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.city
          : undefined,
      plasticProvince:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.province
          : undefined,
      plasticPostal:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.postal
          : undefined,
      plasticCountry:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.country
          : undefined,
      billPayMeta: {
        invoiceNumber: flatFields.invoiceNumber,
        invoiceDate: flatFields.invoiceDateMoment?.format('YYYY-MM-DD'),
        vendorName: flatFields.vendorName,
        vendorEmail:
          flatFields.billPayRecipient === BillPayRecipient.Vendor
            ? flatFields.vendorEmail
            : undefined,
      },
    }
    props.dispatchPostBillPayVirtualCardAndOpenToastAndCloseFlyout({
      ...fields,
      recipient: user?.email,
    })

    analytics?.track('Sucessfully create virtual card', {
      type_of_card: VirtualCardTypes.billPay,
      vendor_name: flatFields.vendorName,
      vendor_email: flatFields.vendorEmail,
      invoice_number: flatFields.invoiceNumber,
      invoice_date: flatFields.invoiceDateMoment?.format('YYYY-MM-DD'),
      credit_limit: flatFields.balanceDollars,
      active_until: flatFields.validToMoment?.format('YYYY-MM-DD'),
      expense_category: Object.keys(expenseDetails).length > 0,
      add_attachments:
        props.receiptAttachments && props.receiptAttachments.length > 0,
      add_notes: !!flatFields.notes,
      is_recipient_me: flatFields.billPayRecipient === BillPayRecipient.Myself,
      receipt_rules_enabled: !!flatFields.receiptRulesExempt,
      issuer:
        creditCards &&
        flatFields.creditCardId &&
        creditCards[flatFields.creditCardId]
          ? creditCards[flatFields.creditCardId].issuer
          : undefined,
    })
  }

  const initialFlatFields: VirtualCardFlatFormValues = {
    // TODO: refactor all uses of queryParams to handle arrays and normalize to string[]
    creditCardId:
      typeof queryParams.creditCardId === 'string'
        ? queryParams.creditCardId
        : undefined,
  }
  return (
    <ActionRightFlyoutContent
      title={<Trns source={'Pay an Invoice'} />}
      specificElements={{
        imageSrc: guestCardImage,
      }}
      pickerSubtitle={
        <Trns source={'Create a virtual card to make an invoice payment'} />
      }
    >
      <VirtualCardForm
        id={'create'}
        buttonText={<Trns source={'Create a Virtual Card'} />}
        billPayShareButtonText={
          <Trns source={'Create Virtual Card and Share with Vendor'} />
        }
        onSuccessfulSubmit={onSuccessfulSubmit}
        loading={props.postingVirtualCard}
        userFieldLabel={<Trns source={'Recipients'} />}
        recipientNamesEditable={false}
        recurrenceEditable={false}
        notesEditable
        creditCardEditable
        amountProps={{
          amountEditable: true,
        }}
        initialFlatFields={initialFlatFields}
        creditCardProps={{
          isFormDisabled: props.isFormDisabled,
          creditCardOptions: props.creditCardOptions,
          labelCreditCardsAsAccounts: props.labelCreditCardsAsAccounts,
        }}
        attachmentsProps={{
          sectionHeader: <Trns source={'Invoice'} />,
          sectionSubheader: (
            <Trns
              source={
                'Use this space to attach a vendor invoice. You will be able to attach the settled invoice once the payment clears.'
              }
            />
          ),
          receiptAttachments: props.receiptAttachments,
          uploadingAttachmentThumbnails: props.uploadingAttachmentThumbnails,
          uploadAttachments: props.uploadAttachments,
          deleteAttachment: props.deleteAttachment,
        }}
        topLevelError={props.topLevelError}
        errorDetailMap={props.errorDetailMap}
        creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
        typeofCard={VirtualCardTypes.billPay}
        showCardApprovalSoftAlert={props.showSoftAlert}
      />
    </ActionRightFlyoutContent>
  )
}

export default CreateVirtualCardBillPayContent
